import * as THREE from "three";

/**
 * Two-layer scrolling city skyline:
 *  - Near layer  (x ≈ ±13 from road centre): full road speed → whips past you
 *  - Far layer   (x ≈ ±38):                  22% speed       → slow parallax
 * Buildings are recycled (teleported to the front) so the city feels endless.
 */

// ---- constants -------------------------------------------------------
const NEAR_FACE_X  = 12;   // distance of building road-facing wall from road centre
const FAR_BASE_X   = 38;   // centre X of far buildings
const FAR_RATIO    = 0.22; // far layer scroll fraction
const RECYCLE_Z    = 24;   // recycle when building's z exceeds this
const NEAR_POOL    = 12;   // buildings per side, near layer
const FAR_POOL     = 10;   // buildings per side, far layer
const NEAR_SPACING = 20;   // metres between near buildings
const FAR_SPACING  = 28;   // metres between far buildings

const BODY_PALETTE = [0x1a1f33, 0x232842, 0x2c2f47, 0x171a2c, 0x222a3f, 0x1c2030];
const NEON_COLORS  = [0xff2a55, 0x00ffe5, 0xb94fff, 0xff8c00, 0x00ff88];
const SIGNS        = ["서울", "K-타운", "쇼", "라이브", "OPEN", "CLUB", "BAR", "24H", "HOT"];

interface Strip { mesh: THREE.Group; z: number; }

// ---- Skyline class ---------------------------------------------------
export class Skyline {
  readonly group: THREE.Group;

  private nearL: Strip[] = [];
  private nearR: Strip[] = [];
  private farL:  Strip[] = [];
  private farR:  Strip[] = [];

  constructor() {
    this.group = new THREE.Group();
    this.buildLayer(this.nearL, -1, NEAR_POOL, NEAR_SPACING, "near");
    this.buildLayer(this.nearR,  1, NEAR_POOL, NEAR_SPACING, "near");
    this.buildLayer(this.farL,  -1, FAR_POOL,  FAR_SPACING,  "far");
    this.buildLayer(this.farR,   1, FAR_POOL,  FAR_SPACING,  "far");
  }

  private buildLayer(
    pool: Strip[], side: 1 | -1, count: number,
    spacing: number, layer: "near" | "far",
  ): void {
    for (let i = 0; i < count; i++) {
      const z    = -i * spacing;
      const mesh = makeBuilding(side, layer);
      mesh.position.z = z;
      this.group.add(mesh);
      pool.push({ mesh, z });
    }
  }

  /** Call every frame with the same dz used for road.scroll(). */
  update(dz: number): void {
    scrollPool(this.nearL, dz,           NEAR_SPACING);
    scrollPool(this.nearR, dz,           NEAR_SPACING);
    scrollPool(this.farL,  dz * FAR_RATIO, FAR_SPACING);
    scrollPool(this.farR,  dz * FAR_RATIO, FAR_SPACING);
  }

  reset(): void {
    resetPool(this.nearL, NEAR_SPACING);
    resetPool(this.nearR, NEAR_SPACING);
    resetPool(this.farL,  FAR_SPACING);
    resetPool(this.farR,  FAR_SPACING);
  }
}

// ---- helpers ---------------------------------------------------------
function scrollPool(pool: Strip[], dz: number, spacing: number): void {
  for (const b of pool) {
    b.z += dz;
    b.mesh.position.z = b.z;
  }
  // Recycle in a second pass so minZ is stable for this frame
  let minZ = Infinity;
  for (const b of pool) if (b.z < minZ) minZ = b.z;
  for (const b of pool) {
    if (b.z > RECYCLE_Z) {
      b.z = minZ - spacing;
      b.mesh.position.z = b.z;
      minZ = b.z; // stagger if multiple recycle at once
    }
  }
}

function resetPool(pool: Strip[], spacing: number): void {
  for (let i = 0; i < pool.length; i++) {
    pool[i].z = -i * spacing;
    pool[i].mesh.position.z = pool[i].z;
  }
}

// ---- building factory ------------------------------------------------
function makeBuilding(side: 1 | -1, layer: "near" | "far"): THREE.Group {
  const g = new THREE.Group();

  const isTower = Math.random() < 0.35;

  // Building thickness (X), height (Y), depth (Z, along road)
  let bw: number, bh: number, bd: number;
  if (layer === "near") {
    bw = isTower ? 4 + Math.random() * 4  : 7 + Math.random() * 7;
    bh = isTower ? 18 + Math.random() * 16 : 9 + Math.random() * 11;
    bd = 10 + Math.random() * 12;
  } else {
    bw = 5 + Math.random() * 12;
    bh = 22 + Math.random() * 22;
    bd = 8 + Math.random() * 14;
  }

  // Position group so the road-facing wall is at ±NEAR_FACE_X (near) or just visible (far)
  const faceX = layer === "near"
    ? NEAR_FACE_X + Math.random() * 3
    : FAR_BASE_X  + Math.random() * 8;
  const groupX = faceX + bw / 2;
  g.position.x = side * groupX;

  // ---- main body ----
  const bodyColor = BODY_PALETTE[Math.floor(Math.random() * BODY_PALETTE.length)];
  const bodyMat = new THREE.MeshStandardMaterial({
    color: bodyColor, roughness: 0.85, metalness: 0.08,
  });
  const body = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd), bodyMat);
  body.position.y = bh / 2;
  g.add(body);

  // ---- window facade facing road ----
  const cols = Math.max(3, Math.round(bd / 2.8));
  const rows = Math.max(4, Math.round(bh / 2.8));
  const tex  = makeWindowTexture(cols, rows);
  const facMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true });
  const facade  = new THREE.Mesh(new THREE.PlaneGeometry(bd * 0.92, bh * 0.84), facMat);
  facade.position.set(-side * (bw / 2 + 0.05), bh * 0.52, 0);
  facade.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2;
  g.add(facade);

  // ---- dark ground-floor overlay ----
  const gfH = Math.min(bh * 0.14, 3.2);
  const gfMat = new THREE.MeshStandardMaterial({ color: 0x0d1017, roughness: 1 });
  const gf = new THREE.Mesh(new THREE.BoxGeometry(bw + 0.1, gfH, bd + 0.1), gfMat);
  gf.position.y = gfH / 2;
  g.add(gf);

  // ---- rooftop detail ----
  if (Math.random() < 0.6) {
    const rtW = bw * (0.15 + Math.random() * 0.35);
    const rtH = 1.2 + Math.random() * 3.5;
    const rtD = bd  * (0.3 + Math.random() * 0.4);
    const rtMat = new THREE.MeshStandardMaterial({ color: 0x161921, roughness: 1 });
    const rt = new THREE.Mesh(new THREE.BoxGeometry(rtW, rtH, rtD), rtMat);
    rt.position.set((Math.random() - 0.5) * bw * 0.5, bh + rtH / 2, (Math.random() - 0.5) * bd * 0.3);
    g.add(rt);

    // sometimes a second rooftop box
    if (Math.random() < 0.4) {
      const rt2 = new THREE.Mesh(new THREE.BoxGeometry(rtW * 0.5, rtH * 0.6, rtD * 0.5), rtMat);
      rt2.position.set((Math.random() - 0.5) * bw * 0.4, bh + rtH * 0.3, (Math.random() - 0.5) * bd * 0.3);
      g.add(rt2);
    }
  }

  // ---- neon sign (near layer only) ----
  if (layer === "near" && Math.random() < 0.48) {
    const text   = SIGNS[Math.floor(Math.random() * SIGNS.length)];
    const nColor = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
    const signTex = makeNeonTexture(text, nColor);
    const signMat = new THREE.MeshBasicMaterial({ map: signTex, transparent: true });
    const sw = 3.5 + Math.random() * 3;
    const sh = 1.4 + Math.random() * 1.4;
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(sw, sh), signMat);
    const sy = bh * (0.45 + Math.random() * 0.35);
    const sz = (Math.random() - 0.5) * bd * 0.55;
    sign.position.set(-side * (bw / 2 + 0.12), sy, sz);
    sign.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2;
    g.add(sign);
  }

  return g;
}

// ---- canvas textures -------------------------------------------------
function makeWindowTexture(cols: number, rows: number): THREE.CanvasTexture {
  const RES = 256;
  const c   = document.createElement("canvas");
  c.width   = RES;
  c.height  = RES;
  const ctx = c.getContext("2d")!;

  // Building facade base
  ctx.fillStyle = "#12161f";
  ctx.fillRect(0, 0, RES, RES);

  // Subtle horizontal floor bands
  const bandH = RES / rows;
  for (let r = 0; r < rows; r++) {
    if (r % 3 === 0) {
      ctx.fillStyle = "rgba(255,255,255,0.018)";
      ctx.fillRect(0, r * bandH, RES, bandH);
    }
  }

  const winW = (RES / cols) * 0.58;
  const winH = (RES / rows) * 0.52;

  // Warm palette (yellows / oranges) and cool palette (blues / purples)
  const warm = ["#fff2c0", "#ffc77a", "#ffaa55", "#ffd060"];
  const cool = ["#4dd0e1", "#c77dff", "#7ae4ff", "#a0b0ff"];

  for (let r = 0; r < rows; r++) {
    const isGround = r === rows - 1;
    for (let col = 0; col < cols; col++) {
      const cx = (col + 0.5) * (RES / cols);
      const cy = (r   + 0.5) * (RES / rows);

      if (isGround) {
        // Storefront: wide openings
        const open = Math.random() > 0.25;
        ctx.fillStyle = open ? warm[Math.floor(Math.random() * warm.length)] : "#090c12";
        const sfW = (RES / cols) * 0.82;
        const sfH = bandH * 0.62;
        ctx.fillRect(cx - sfW / 2, cy - sfH / 2, sfW, sfH);
      } else {
        const lit   = Math.random() > 0.3;
        if (lit) {
          const palette = Math.random() < 0.65 ? warm : cool;
          const col2 = palette[Math.floor(Math.random() * palette.length)];
          ctx.shadowBlur  = 4;
          ctx.shadowColor = col2;
          ctx.fillStyle   = col2;
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle  = "#090c14";
        }
        ctx.fillRect(cx - winW / 2, cy - winH / 2, winW, winH);
        ctx.shadowBlur = 0;
      }
    }
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeNeonTexture(text: string, color: number): THREE.CanvasTexture {
  const c   = document.createElement("canvas");
  c.width   = 512;
  c.height  = 192;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, c.width, c.height);

  // Semi-transparent backplate
  ctx.fillStyle = "rgba(6,4,16,0.88)";
  ctx.fillRect(8, 8, c.width - 16, c.height - 16);

  const r   = (color >> 16) & 0xff;
  const gv  = (color >>  8) & 0xff;
  const b   = color & 0xff;
  const css = `rgb(${r},${gv},${b})`;

  ctx.font         = "bold 96px sans-serif";
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";

  // Outer glow
  ctx.shadowBlur  = 55;
  ctx.shadowColor = css;
  ctx.fillStyle   = "#ffffff";
  ctx.fillText(text, c.width / 2, c.height / 2);

  // Coloured stroke
  ctx.shadowBlur  = 25;
  ctx.shadowColor = css;
  ctx.strokeStyle = css;
  ctx.lineWidth   = 2.5;
  ctx.strokeText(text, c.width / 2, c.height / 2);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
